import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Alert,
  Button,
  ConfirmDialog,
  Modal,
  toast,
  useWizard,
} from "@picklebet/ui";
import { constructCurrency } from "common/currency";
import { parseError } from "@/utilities/errorsParser";
import { walletService } from "@/features/wallet/services/wallet";
import { useWithdrawAmountValidationSchema } from "@/features/wallet/hooks/useWithdrawAmountValidationSchema";
import { useBankAccount } from "@/hooks/firestore/user/useBankAccounts";
import { useWithdrawals } from "@/hooks/firestore/user/useWithdrawals";
import { useProfile } from "@/hooks/firestore/user/useProfile";
import { Link } from "@/features/shell/components/shared/Link/Link";
import { useIsPhoneVerificationEnabled } from "@/features/auth/hooks/useIsPhoneVerificationEnabled";
import { PHONE_VERIFIED_STATUSES } from "@/features/account/types/Verification";
import { useReportSubmitting } from "@/features/wallet/hooks/useReportSubmitting";
import { useWithdrawWizard } from "@/features/wallet/components/withdraw/hooks/useWithdrawWizard";
import { useAudWithdrawal } from "@/features/wallet/components/withdraw/hooks/useAudWithdrawal";
import type { WithdrawStepName } from "@/features/wallet/components/withdraw/utils/registry";
import { WithdrawContent } from "../../blocks/withdraw-content/WithdrawContent";
import { WithdrawAmountInput } from "../../blocks/withdraw-amount-input/WithdrawAmountInput";
import {
  WithdrawableBalance,
  WithdrawableBalanceSkeleton,
} from "../../blocks/withdrawable-balance/WithdrawableBalance";
import { SectionDivider } from "../../blocks/section-divider/SectionDivider";
import { SectionHeader } from "../../blocks/section-header/SectionHeader";

import * as styles from "./AudWithdrawView.module.scss";
import {
  MethodRow,
  MethodRowSkeleton,
} from "@/features/wallet/components/deposit/components/deposit-wizard/method-row/MethodRow";
import { BankMethodIcon } from "@/features/wallet/components/deposit/components/deposit-wizard/method-row/BankMethodIcon";
import { RemoveAction } from "@/features/wallet/components/deposit/components/deposit-wizard/method-row/RemoveAction";
import { OptionRowAdd } from "@/features/wallet/components/deposit/components/deposit-wizard/option-row/OptionAddRow";
import { AmountInputSkeleton } from "@/features/wallet/components/shared/amount-input-skeleton/AmountInputSkeleton";

// mirrors the settled view block for block: any row missing here becomes a
// jump in modal height when the data lands
export const AudWithdrawViewSkeleton = () => (
  <WithdrawContent>
    <AmountInputSkeleton />
    <SectionDivider />
    <SectionHeader>Bank Account</SectionHeader>
    <MethodRowSkeleton />
    <Modal.Footer>
      <Button className={styles.depositButton} skeleton>
        Withdraw
      </Button>
    </Modal.Footer>
    <WithdrawableBalanceSkeleton />
  </WithdrawContent>
);

export const AudWithdrawView = () => {
  const { amount, setAmount, currency, setSubmitting } = useWithdrawWizard();
  const { push, goTo } = useWizard<WithdrawStepName>();
  const { bankAccount, isLoading: bankAccountLoading } = useBankAccount();
  const { withdrawals } = useWithdrawals();
  const { phoneDetails } = useProfile();
  const { isEnabled: isVerificationEnabled } = useIsPhoneVerificationEnabled();
  const { submit } = useAudWithdrawal();

  const [schema, schemaLoading] = useWithdrawAmountValidationSchema();
  const [isRemoveOpen, setIsRemoveOpen] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { amount },
  });

  useReportSubmitting(isSubmitting, setSubmitting);

  const isVerificationRequired =
    isVerificationEnabled &&
    phoneDetails?.verificationStatus &&
    !PHONE_VERIFIED_STATUSES.includes(phoneDetails.verificationStatus);

  const onSubmit = async (values: { amount: number }) => {
    if (isVerificationRequired) {
      // the amount already lives in wizard context, so the OTP step can submit
      push("withdraw/verify-mobile");
      return;
    }

    const result = await submit(values.amount);

    if (!result.ok) {
      toast({
        variant: "danger",
        title: "Withdrawal Failed",
        description: parseError(result.error),
      });
    }
  };

  const handleRemoveBankAccount = async () => {
    if (!bankAccount?.id) return;

    if (withdrawals?.some((withdrawal) => withdrawal.status === "PENDING")) {
      toast({
        description:
          "You cannot remove a bank account while you have a current pending withdrawal",
        variant: "danger",
      });
      setIsRemoveOpen(false);
      return;
    }

    await walletService.removeBankAccount();
    setIsRemoveOpen(false);
    goTo("withdraw/bank-details", { replace: true });
  };

  if (schemaLoading || bankAccountLoading) {
    return <AudWithdrawViewSkeleton />;
  }

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <WithdrawContent>
          {isVerificationRequired && (
            <Alert size="small">
              <div>
                Your mobile number requires verification to withdraw. Verify
                during your next withdrawal, or via the{" "}
                <Link className={styles.blueLink} to="/account/details">
                  Details page
                </Link>{" "}
                now.
              </div>
            </Alert>
          )}

          <Controller
            control={control}
            name="amount"
            render={({ field: { onChange, value } }) => (
              <WithdrawAmountInput
                value={value}
                onChange={(next) => {
                  onChange(next);
                  setAmount(next);
                }}
                errorMessage={errors.amount?.message}
              />
            )}
          />

          <SectionDivider />

          <SectionHeader>Bank Account</SectionHeader>

          {bankAccount ? (
            <MethodRow
              icon={<BankMethodIcon />}
              label={bankAccount.name}
              sublabel={`${bankAccount.bsb} - ${bankAccount.number}`}
              trailing={<RemoveAction onClick={() => setIsRemoveOpen(true)} />}
            />
          ) : (
            <OptionRowAdd
              onClick={() => goTo("withdraw/bank-details")}
              label="Add Bank Account"
            />
          )}

          <Modal.Footer>
            <Button
              className={styles.depositButton}
              type="submit"
              disabled={!amount || isSubmitting}
              loading={isSubmitting}
            >
              Withdraw{" "}
              {constructCurrency(amount * 100, { currencyType: currency })}
            </Button>
          </Modal.Footer>

          <WithdrawableBalance />
        </WithdrawContent>
      </form>

      <ConfirmDialog
        onConfirm={handleRemoveBankAccount}
        onClose={() => setIsRemoveOpen(false)}
        isOpen={isRemoveOpen}
        title={`Remove Bank Account`}
        description={`Are you sure you want to remove your bank account details?`}
      />
    </>
  );
};
